import {
  formatDurationToIsoString,
  formatTimestampToIsoString,
} from '../../utils/datetime.js';
import { config } from '../../config.js';
import { getImageDefaultSrc } from '../../helpers/images.js';
import { participantsBySeriesId } from '../../modules/pages/participants.js';

import {
  getAssetAccessLevel,
  getAssetFlightTimesStart,
  getAssetMainImage,
  getAssetTags,
  getAssetUrl,
  getAssetVerticalImage,
  getSeoAssetDescription,
  getSeoAssetTitle,
  isDocumentary,
} from './assetHelpers.js';
import { getPath, pathToUrl, routeNames } from './routes.js';

import type { SvpCategoryAdditional } from '@vgtv/api-client/lib/svp_category';
import type { SvpSeasonSliderComponent } from '@vgtv/api-client/lib/component/slider';
import type {
  Clip,
  CreativeWork,
  ItemList,
  Movie,
  NewsArticle,
  TVEpisode,
  TVSeries,
  VideoObject,
  WithContext,
} from 'schema-dts';
import type {
  SvpAsset,
  SvpAssetAdditional,
} from '@vgtv/api-client/lib/svp_asset';

/**
 * Skips keys with undefined or null values
 */
function normalizeObject<T extends object>(blob: T) {
  return Object.entries(blob)
    .filter(([_, v]) => v !== undefined && v !== null)
    .reduce((object, [key, value]) => {
      return { ...object, [key]: value };
    }, {} as T);
}

function isLiveBroadcast(asset: SvpAsset): boolean {
  return asset.streamType === 'live' || asset.streamType === 'wasLive';
}

function assetTagsToKeywords(asset: SvpAsset) {
  return getAssetTags(asset as SvpAssetAdditional)
    .map((tag) => tag.tag)
    .join(',');
}

// This type is not properly parsed by Google rich results so you can't rely on nested episodes here
function getTVSeriesStructuredData(
  category: SvpCategoryAdditional,
  componentConfig: SvpSeasonSliderComponent
) {
  const tvSeriesObject: WithContext<TVSeries> = {
    '@type': 'TVSeries',
    '@context': 'https://schema.org',
    name: category.title,
    description: category.additional?.description || '',
    numberOfSeasons: componentConfig.seasons.length,
    containsSeason: componentConfig.seasons.map((season) => ({
      '@type': 'TVSeason',
      name: season.title,
      seasonNumber: season.seasonNumber,
    })),
  };
  return normalizeObject(tvSeriesObject);
}

function getCreativeWorkProperties(asset: SvpAssetAdditional) {
  const thumbnailUrl = `${getAssetMainImage(asset)}?t[]=1280x720`;
  const publicationDate = getAssetFlightTimesStart(asset) || asset.published;

  const title = getSeoAssetTitle(asset);

  const creativeWorkObject: WithContext<CreativeWork> = {
    '@type': 'CreativeWork',
    '@context': 'https://schema.org',
    name: title,
    datePublished: publicationDate
      ? formatTimestampToIsoString(publicationDate)
      : undefined,
    dateCreated: asset.created
      ? formatTimestampToIsoString(asset.created)
      : undefined,
    dateModified: asset.updated
      ? formatTimestampToIsoString(asset.updated)
      : undefined,
    thumbnailUrl,
    description: getSeoAssetDescription(asset) || title,
  };

  return creativeWorkObject;
}

function getTVEpisodeStructuredData(asset: SvpAssetAdditional) {
  const tvEpisodeObject: WithContext<TVEpisode> = {
    ...getCreativeWorkProperties(asset),
    '@type': 'TVEpisode',
    position: asset.series?.episodeNumber,
    video: getVideoObjectStructuredData(asset),
    partOfSeries: {
      '@type': 'TVSeries',
      name: asset.category.title,
    },
    partOfSeason: {
      '@type': 'TVSeason',
      name: `Season ${asset.series?.seasonNumber}`,
    },
  };
  return tvEpisodeObject;
}

function getMovieStructuredData(asset: SvpAssetAdditional) {
  const image = getImageDefaultSrc(
    getAssetVerticalImage(asset)!,
    'assetsSliderVertical'
  );

  const movieObject: WithContext<Movie> = {
    ...getCreativeWorkProperties(asset),
    '@type': 'Movie',
    image,
  };

  return movieObject;
}

const getParticipants = (asset: SvpAssetAdditional) => {
  if (asset.category.id === 100441 && asset.series?.seasonNumber === 4) {
    return participantsBySeriesId[asset.category.id];
  }

  if (asset.category.id === 100528 && asset.series?.seasonNumber === 1) {
    return participantsBySeriesId[asset.category.id];
  }

  if (asset.category.id === 100522 && asset.series?.seasonNumber === 1) {
    return participantsBySeriesId[asset.category.id];
  }

  return undefined;
};

const getActorProperty = (
  asset: SvpAssetAdditional
): WithContext<VideoObject>['actor'] => {
  return getParticipants(asset)?.map(({ name, description, images }) => {
    return {
      '@type': 'Person',
      name,
      description,
      image: `${images.thumbnail}?t[]=720x1280`,
    };
  });
};

/**
 * Transforms asset into video object
 */
function getVideoObjectStructuredData(asset: SvpAssetAdditional) {
  const videoObject: WithContext<VideoObject> = {
    ...getCreativeWorkProperties(asset),
    // properties of VideoObject
    '@id': getAssetUrl(config.provider, asset),
    '@type': 'VideoObject',
    actor: getActorProperty(asset),
    uploadDate: asset.published
      ? formatTimestampToIsoString(asset.published)
      : undefined,
  };

  if (asset.additional) {
    const tags = assetTagsToKeywords(asset);
    if (tags) {
      videoObject.keywords = tags;
    }
  }

  const contentUrl = asset.streamUrls?.mp4 ?? asset.streamUrls?.hls;
  if (contentUrl && !getAssetAccessLevel(asset)) {
    videoObject.contentUrl = contentUrl;
  } else {
    // Fall back to poster image if the video is paywalled (google won't be able to access it anyway)
    videoObject.contentUrl = videoObject.thumbnailUrl as string;
  }

  if (asset.flightTimes && asset.flightTimes.end) {
    videoObject.expires = formatTimestampToIsoString(asset.flightTimes.end);
  }

  if (asset.displays) {
    videoObject.interactionStatistic = {
      '@type': 'InteractionCounter',
      interactionType: { '@type': 'WatchAction' },
      userInteractionCount: asset.displays,
    };
  }

  const oneSecondInMilliseconds = 1000;

  if (asset.duration && asset.duration >= oneSecondInMilliseconds) {
    videoObject.duration = formatDurationToIsoString(asset.duration);
  }

  if (isLiveBroadcast(asset) && asset.flightTimes?.start) {
    const assetFlightTimesStart = getAssetFlightTimesStart(asset)!;

    // end date is required, but is not provided by svp api - we assume the live stream will be 2 hours long
    const twoHoursInSeconds = 2 * 60 * 60;

    videoObject.publication = {
      '@type': 'BroadcastEvent',
      isLiveBroadcast: isLiveBroadcast(asset),
      startDate: formatTimestampToIsoString(assetFlightTimesStart),
      endDate: formatTimestampToIsoString(
        assetFlightTimesStart + twoHoursInSeconds
      ),
    };
  }
  if (
    asset.additional &&
    asset.additional.chapters &&
    asset.additional.chapters.length > 0
  ) {
    const chapters = asset.additional.chapters;

    videoObject.hasPart = chapters.map((chapter, i): Clip => {
      return {
        '@type': 'Clip',
        name: chapter.title,
        startOffset: chapter.timeline,
        endOffset:
          chapters.length > i + 1
            ? chapters[i + 1].timeline // beginning of the next clip
            : Math.floor(asset.duration / 1000), // end of stream
        url: `${getAssetUrl(config.provider, asset)}/${i + 1}`,
      };
    });
  }

  return normalizeObject(videoObject);
}

function getAssetStructuredData(
  asset: SvpAssetAdditional,
  isNewsAsset?: boolean
) {
  let data: WithContext<CreativeWork> | undefined;
  if (isDocumentary(asset)) {
    data = getMovieStructuredData(asset);
  } else if (isNewsAsset) {
    data = getNewsStructuredData(asset);
  } else {
    data = getVideoObjectStructuredData(asset);
  }

  return normalizeObject(data);
}

/**
 * Transforms assets into item list object
 */
function getMoviesItemListStructuredData(
  assets: SvpAssetAdditional[]
): WithContext<ItemList> | undefined {
  const movies = assets.filter((asset) => isDocumentary(asset));

  if (!movies) {
    return;
  }

  const itemList = movies.map((asset, index) => {
    return {
      position: index,
      ...getMovieStructuredData(asset),
      url: getAssetUrl(config.provider, asset),
    };
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: itemList,
  };
}

/**
 * Transforms assets into item list object
 */
function getAssetsItemListStructuredData(
  assets: SvpAssetAdditional[]
): WithContext<ItemList> {
  const itemList = assets.map((asset, index) => {
    return {
      position: index,
      ...getVideoObjectStructuredData(asset),
      url: getAssetUrl(config.provider, asset),
    };
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: itemList,
  };
}

function getNewsStructuredData(
  asset: SvpAssetAdditional
): WithContext<NewsArticle> {
  return {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: asset.title,
    datePublished: asset.published
      ? formatTimestampToIsoString(asset.published)
      : undefined,
    dateModified: asset.updated
      ? formatTimestampToIsoString(asset.updated)
      : undefined,
    wordCount: asset.description?.trim().split(/\s+/g).length,
    description: asset.description ?? '',
    keywords: assetTagsToKeywords(asset),
    image: {
      '@type': 'ImageObject',
      url: `${getAssetMainImage(asset)}?t[]=1280x720`,
    },
    video: getVideoObjectStructuredData(asset),
    mainEntityOfPage: pathToUrl(
      getPath(routeNames.asset, {
        asset,
        skipDeeplinkCheck: true,
      })
    ),
  };
}

export {
  // Asset
  getAssetStructuredData,
  getTVEpisodeStructuredData,
  // Sliders
  getMoviesItemListStructuredData,
  getAssetsItemListStructuredData,
  getTVSeriesStructuredData,
};
